/* eslint-disable no-restricted-imports */
import type { AxiosError } from 'axios'
import axios from 'axios'
import type { AuthPort } from '@/domain/auth/AuthPort'
import { useSocietyStore } from '@/store/society'
import { rememberReturnPath } from '@/utils/returnPath'

class HttpRequest {
  /**
   * `undefined`, NUNCA `null`: axios serializa un body `null` como el JSON literal `null`, y con
   * `Content-Type: application/json` DRF lo parsea a `None`. Un endpoint con InputSerializer lo
   * rechaza con "No data provided" antes de correr una línea de su propia lógica — que es lo que
   * hacía fallar a los 3 POST sin body de la revalidación MX. Con `undefined` axios no manda body y
   * `request.data` llega vacío, que es lo que esos serializers esperan.
   */
  private _body: object | FormData | undefined = undefined
  private _params: Record<string, string | string[]> = {}
  private _prefix = 'api'
  /** `undefined` keeps axios' own default, which is no timeout at all. */
  private _timeout: number | undefined = undefined
  private _signal: AbortSignal | undefined = undefined
  private _headers: Record<string, string> = {}

  constructor(
    private auth: AuthPort,
    private _url: string,
  ) {}

  /**
   * @deprecated Don't pass a prefix. The base path is `api` by default, so include the rest of the
   * path in `url()` directly — e.g. `url('purchases/requests')`, not `prefix('api/purchases').url('requests')`.
   * Kept only so legacy call sites keep compiling. See docs/MIGRATION.md.
   */
  prefix(value: string) {
    this._prefix = value
    return this
  }

  body(value: object | FormData) {
    this._body = value
    return this
  }

  /** A list becomes a repeated param (`key=a&key=b`), for values that can carry commas. */
  params(value: Record<string, string | string[]>) {
    this._params = { ...this._params, ...value }
    return this
  }

  /** Milliseconds before the request is abandoned. Only for endpoints that can legitimately take
   *  long enough that waiting forever is worse than failing. */
  timeout(ms: number) {
    this._timeout = ms
    return this
  }

  signal(value: AbortSignal) {
    this._signal = value
    return this
  }

  /** An extra header for this call only, e.g. `Idempotency-Key`. Auth and locale stay owned by `call()`. */
  header(name: string, value: string) {
    this._headers = { ...this._headers, [name]: value }
    return this
  }

  async get<T = unknown>(): Promise<T> {
    return this.call<T>('get')
  }

  async post<T = unknown>(): Promise<T> {
    return this.call<T>('post')
  }

  async put<T = unknown>(): Promise<T> {
    return this.call<T>('put')
  }

  async patch<T = unknown>(): Promise<T> {
    return this.call<T>('patch')
  }

  async delete<T = unknown>(): Promise<T> {
    return this.call<T>('delete')
  }

  private async call<T>(method: 'get' | 'post' | 'put' | 'patch' | 'delete'): Promise<T> {
    const token = await this.auth.getBearerToken()
    const locale = localStorage.getItem('locale') || 'es'
    const url = `${import.meta.env.VITE_API_URL}/${this._prefix}/${this._url}/`

    const headers: Record<string, string> = {
      ...this._headers,
      'Accept-Language': locale,
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    if (!(this._body instanceof FormData)) {
      headers['Content-Type'] = 'application/json'
    }

    const config = { headers, params: this._params, paramsSerializer: { indexes: null }, ...(this._timeout !== undefined ? { timeout: this._timeout } : {}), ...(this._signal ? { signal: this._signal } : {}) }

    try {
      let response

      switch (method) {
        case 'get':
          response = await axios.get(url, config)
          break
        case 'post':
          response = await axios.post(url, this._body, config)
          break
        case 'put':
          response = await axios.put(url, this._body, config)
          break
        case 'patch':
          response = await axios.patch(url, this._body, config)
          break
        case 'delete':
          response = await axios.delete(url, config)
          break
      }

      // Mirrors `@/utils/api.ts:28-30` — keeps the society store in sync.
      const envData = (response!.data as { environment?: { society_id: number, plan: string } })?.environment
      if (envData) {
        const societyStore = useSocietyStore()
        societyStore.setId(envData.society_id)
        societyStore.setPlan(envData.plan)
      }

      return response!.data as T
    } catch (e) {
      const error = e as AxiosError
      const detail = (error.response?.data as { detail?: string })?.detail

      if (error.response?.status === 401) {
        await this.auth.signOut()
        rememberReturnPath(window.location.pathname + window.location.search)
        window.location.href = '/auth/login'
        throw error
      }

      // Mirrors `@/utils/api.ts:36-37`. BE sentinel string kept misspelled ("resourse") for compatibility.
      if (detail === 'Upgrade plan to access this resourse') {
        window.location.href = '/upgrade_plan'
        throw error
      }

      // Mirrors `@/utils/api.ts:38-39`.
      if (detail === 'Password expired') {
        window.location.href = '/auth/password_expired'
        throw error
      }

      throw e
    }
  }
}

export class HttpClient {
  constructor(private auth: AuthPort) {}

  url(path: string): HttpRequest {
    return new HttpRequest(this.auth, path)
  }
}
